import React, { useMemo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { calculateActivityHeatmap } from '../lib/heatmapCalculator';

const { FiActivity, FiX, FiZap, FiCalendar, FiAward, FiBookmark } = FiIcons;

const INTENSITY_COLORS = [
  '#16161d', // level 0
  '#064e3b', // level 1
  '#047857', // level 2
  '#10b981', // level 3
  '#34d399', // level 4 (brightest)
];

export default function ActivityHeatmapModal({
  isOpen,
  onClose,
  data = {},
  themeColors: tc,
}) {
  const [hoveredCell, setHoveredCell] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const stats = useMemo(() => {
    const all = [];
    Object.values(data).forEach((f) => {
      (f.items || []).forEach((item) => {
        if (item.type === 'link') all.push(item);
      });
    });
    return calculateActivityHeatmap(all);
  }, [data]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative w-full max-w-3xl rounded-2xl bg-[#121216] border border-[#26262e] shadow-2xl p-6 z-10 flex flex-col font-sans"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-[#24242c]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiActivity} className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Cognitive Activity Horizon</h3>
                <p className="text-[10px] text-[#777] font-mono">
                  52-Week Research & Engagement Velocity
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-[#888] hover:text-white rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-3 my-4">
            <div className="p-3 rounded-xl bg-[#171720] border border-[#22222a] flex flex-col">
              <span className="text-[10px] text-[#777] uppercase tracking-wider flex items-center gap-1 font-mono">
                <SafeIcon icon={FiBookmark} className="w-3 h-3 text-emerald-400" />
                Library Size
              </span>
              <span className="text-lg font-bold text-white mt-1 font-mono">
                {stats.totalBookmarks}
              </span>
            </div>
            <div className="p-3 rounded-xl bg-[#171720] border border-[#22222a] flex flex-col">
              <span className="text-[10px] text-[#777] uppercase tracking-wider flex items-center gap-1 font-mono">
                <SafeIcon icon={FiZap} className="w-3 h-3 text-amber-400" />
                Total Launches
              </span>
              <span className="text-lg font-bold text-white mt-1 font-mono">
                {stats.totalClicks}
              </span>
            </div>
            <div className="p-3 rounded-xl bg-[#171720] border border-[#22222a] flex flex-col">
              <span className="text-[10px] text-[#777] uppercase tracking-wider flex items-center gap-1 font-mono">
                <SafeIcon icon={FiCalendar} className="w-3 h-3 text-purple-400" />
                Active Days
              </span>
              <span className="text-lg font-bold text-white mt-1 font-mono">
                {stats.activeDays}
              </span>
            </div>
            <div className="p-3 rounded-xl bg-[#171720] border border-[#22222a] flex flex-col">
              <span className="text-[10px] text-[#777] uppercase tracking-wider flex items-center gap-1 font-mono">
                <SafeIcon icon={FiAward} className="w-3 h-3 text-rose-400" />
                Peak Streak
              </span>
              <span className="text-lg font-bold text-white mt-1 font-mono">
                {stats.maxStreak}d
              </span>
            </div>
          </div>

          {/* Heatmap Grid */}
          <div className="p-4 rounded-xl bg-[#0a0a0d] border border-[#1e1e24] overflow-x-auto">
            <div className="flex gap-1 justify-between min-w-[620px]">
              {stats.weeks.map((week, wIdx) => (
                <div key={wIdx} className="flex flex-col gap-1">
                  {week.map((day, dIdx) => (
                    <div
                      key={dIdx}
                      onMouseEnter={() => setHoveredCell(day)}
                      onMouseLeave={() => setHoveredCell(null)}
                      className="w-2.5 h-2.5 rounded-sm transition-transform hover:scale-125 hover:z-10 cursor-pointer"
                      style={{ backgroundColor: INTENSITY_COLORS[day.level] }}
                      title={`${day.date}: ${day.count} activities`}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Hover Tooltip & Legend */}
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#1a1a22] text-[10px] font-mono text-[#777]">
              <div>
                {hoveredCell ? (
                  <span className="text-emerald-400">
                    {hoveredCell.date} • {hoveredCell.count} recorded interactions
                  </span>
                ) : (
                  <span>Hover any cell to inspect activity density</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span>Less</span>
                {INTENSITY_COLORS.map((c, i) => (
                  <div key={i} className="w-2 h-2 rounded-sm" style={{ backgroundColor: c }} />
                ))}
                <span>More</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
